import * as basket from "../eaiws/basket";
import { CalculationSheetInfo } from "../eaiws/basket";
import { ListenableEvent } from "../utils";
import { Lock } from "../utils/async";
import { removeElement } from "../utils/array";
import { ERR_INVALID_ARGUMENT, ERR_INVALID_STATE, newError, rejectError } from "../utils/error";
import { isNullOrEmpty } from "../utils/string";
import { ItemUpdateFlags } from "./BaseItem";
export class BaseItemManager {
    get root() {
        if (this.mRoot == null) {
            throw newError(ERR_INVALID_STATE, "root not initialized or already cleared");
        }
        return (this.mRoot);
    }
    get session() {
        return (this.mSession);
    }
    get selection() {
        return (this.mSelection);
    }
    get isValid() {
        return (this.mSession.isValid && this.mRoot != null);
    }
    constructor(pSession) {
        this.eventSelectionChanged = new ListenableEvent(this);
        /**
        * Triggered every time the header calculation has changed.
        * Note: A changed header calculation may also imply that the calculation of all items has changed if
        * some conditions depend on header conditions. The event `eventItemUpdated` may not be triggered
        * for all items in these cases (e.g. for indirect changes).
        */
        this.eventHeaderCalculationChanged = new ListenableEvent(this);
        this.eventItemCreated = new ListenableEvent(this);
        this.eventItemDisposed = new ListenableEvent(this);
        /**
        * Triggered every time an item changed.
        * Note: The event will not be triggered if the item calculation has indirectly changed by
        * some header conditions. You should also listen to the event `eventHeaderCalculationChanged`.
        */
        this.eventItemUpdated = new ListenableEvent(this);
        this.mSession = pSession;
        this.mRoot = undefined;
        this.mItemMap = new Map();
        this.mSelection = new Array();
        this.mSelectionChanged = false;
        this.mSelectionChangeDepth = 0;
        this.mCacheDepth = 0;
        this.mItemUpdateDepth = 0;
        this.mItemUpdatePromise = null;
        this.mItemUpdatePromiseResolve = null;
        this.mPropsCache = new Map();
        this.mEaiwsItemCache = new Map();
        this.mCalculationCache = new Map();
        this.mHeaderCalculation = undefined;
        this.mHeaderCalcVersion = 0;
        this._fetchItemPropertiesLock = new Lock();
        this._fetchCalculationsLock = new Lock();
        this._fetchItemFieldsLock = new Lock();
        this._deletedItemsCache = new Map();
    }
    getHeaderCalculation() {
        const tProcedure = this._getPricingProcedure();
        if (tProcedure == null)
            return (rejectError(ERR_INVALID_STATE, "no active pricing procedure"));
        if (this.mHeaderCalculation != null)
            return (Promise.resolve(this.mHeaderCalculation));
        let tOptions;
        if (this.mViewId != null) {
            tOptions = new basket.GetPriceCalculationSheetOptions();
            tOptions.viewId = this.mViewId;
        }
        const tPromise = this.mSession.basket.getPriceCalculationSheet([""], tProcedure.name, tOptions).
            then((pSheet) => {
            const tCalculation = new CalculationSheetInfo(tProcedure, pSheet);
            this.mHeaderCalculation = tCalculation;
            return (tCalculation);
        });
        return (tPromise);
    }
    invalidateHeaderCalculation() {
        this.mHeaderCalculation = undefined;
        this.mHeaderCalcVersion++;
        this.eventHeaderCalculationChanged.trigger();
    }
    getItemById(pItemId) {
        const tItem = this.mItemMap.get(pItemId);
        return (tItem != null ? tItem : null);
    }
    setSelection(pSelection) {
        let tChanged = false;
        //deselect first
        if (this.mSelection.length > 0) {
            for (const tItem of this.mSelection) {
                tItem._setIsSelected(false);
            }
            this.mSelection = new Array();
            tChanged = true;
        }
        if (pSelection != null && pSelection.length > 0) {
            for (const tItem of pSelection) {
                if (!tItem.isDisposed && !tItem.isSelected) {
                    this.mSelection.push(tItem);
                    tItem._setIsSelected(true);
                }
            }
            tChanged = true;
        }
        if (tChanged)
            this.selectionChanged();
        return (tChanged);
    }
    addToSelection(pItem) {
        if (pItem == null || pItem.isSelected || pItem.isDisposed)
            return (false);
        this.mSelection.push(pItem);
        pItem._setIsSelected(true);
        this.selectionChanged();
        return (true);
    }
    removeFromSelection(pItem) {
        if (pItem == null || !pItem.isSelected)
            return (false);
        if (removeElement(this.mSelection, pItem)) {
            pItem._setIsSelected(false);
            this.selectionChanged();
            return (true);
        }
        return (false);
    }
    beginSelectionChange() {
        this.mSelectionChangeDepth++;
    }
    endSelectionChange() {
        this.mSelectionChangeDepth--;
        if (this.mSelectionChangeDepth <= 0) {
            this.mSelectionChangeDepth = 0;
            if (this.mSelectionChanged)
                this.selectionChanged();
        }
    }
    waitUntilItemsUpdated() {
        if (this.mItemUpdatePromise == null)
            return (Promise.resolve());
        else
            return (this.mItemUpdatePromise);
    }
    /** @internal */
    _getViewId() {
        return (this.mViewId);
    }
    /** @internal */
    _registerItem(pItem) {
        if (pItem != null && this.mItemMap != null) {
            this.mItemMap.set(pItem.id, pItem);
        }
    }
    /** @internal */
    _deregisterItem(pItem) {
        if (pItem != null && this.mItemMap != null) {
            this.mItemMap.delete(pItem.id);
            this.removeFromSelection(pItem);
        }
    }
    /** @internal */
    async _createItem(pItemId, pParent, pChildIndex, pTriggerEvent) {
        if (isNullOrEmpty(pItemId))
            throw (new Error(ERR_INVALID_ARGUMENT));
        try {
            this._beginItemUpdate();
            this._beginCaching();
            await this._fetchEaiwsItems([pItemId], true, false);
            const tEaiwsItem = this._getCachedEaiwsItem(pItemId);
            if (tEaiwsItem == null)
                throw new Error("Failed to get eaiws item: " + pItemId);
            const tItem = this.createItem(pParent, pChildIndex, tEaiwsItem);
            //create child items
            await tItem._rebuildChildList(ItemUpdateFlags.None);
            if (pTriggerEvent)
                this.eventItemCreated.trigger(tItem);
            return (tItem);
        }
        finally {
            this._endCaching();
            this._endItemUpdate();
        }
    }
    /** @internal */
    async _getEaiwsItem(pItemId) {
        if (isNullOrEmpty(pItemId))
            throw new Error(ERR_INVALID_ARGUMENT);
        try {
            this._beginCaching();
            await this._fetchEaiwsItems([pItemId], false, false);
            const tItem = this._getCachedEaiwsItem(pItemId);
            if (tItem == null)
                throw new Error("Failed to get eaiws item: " + pItemId);
            return (tItem);
        }
        finally {
            this._endCaching();
        }
    }
    /** @internal */
    _fetchEaiwsItems(pItemIds, pSubItems, pWholeComposite) {
        if (!this._isCaching())
            return (rejectError(ERR_INVALID_STATE, "caching not enabled"));
        //collect uncached items
        const tItemIds = this.getUncachedItemIds(pItemIds, this.mEaiwsItemCache);
        if (tItemIds.length <= 0)
            return (Promise.resolve());
        const tOptions = new basket.GetAllItemsOptions();
        tOptions.setArticleIds = true;
        tOptions.geometryIds = true;
        tOptions.subItems = pSubItems;
        tOptions.wholeComposite = pWholeComposite;
        if (this.mViewId != null)
            tOptions.viewId = this.mViewId;
        const tPromise = this.mSession.basket.getAllItems(tItemIds, tOptions).then(pResult => {
            //fill cache
            for (const tItem of pResult)
                this._addEaiwsItemToCache(tItem);
        });
        return (tPromise);
    }
    /** @internal */
    _getCachedEaiwsItem(pItemId) {
        return (this.mEaiwsItemCache.get(pItemId));
    }
    /** @internal */
    _fetchItemProps(pItems) {
        if (!this._isCaching())
            return (rejectError(ERR_INVALID_STATE, "caching not enabled"));
        //collect uncached items
        const tItemIds = this.getUncachedItemIds(pItems.map((item) => item.id), this.mPropsCache);
        if (tItemIds.length <= 0)
            return (Promise.resolve());
        const tOptions = new basket.GetItemPropertiesOptions();
        tOptions.separateCurrencies = true;
        tOptions.inactivePositionState = true;
        tOptions.includeCalculationErrorsInInconsistencies = this.includeCalculationErrorsInInconsistencies;
        if (this.mViewId != null) {
            tOptions.viewId = this.mViewId;
            const tPriceProc = this._getPricingProcedure();
            if (tPriceProc != null) {
                tOptions.priceInfo = true;
                tOptions.pricingProcedureName = tPriceProc.name;
                tOptions.extraPriceInfoSelectors = this.mExtraPriceInfoSelectors;
            }
        }
        const tPromise = this.mSession.basket.getItemProperties(tItemIds, tOptions).then(pResult => {
            //fill cache
            for (const tProps of pResult) {
                if (tProps.itemId != null) {
                    this.mPropsCache.set(tProps.itemId, tProps);
                }
            }
        });
        return (tPromise);
    }
    /** @internal */
    _getCachedItemProps(pItemId) {
        return (this.mPropsCache.get(pItemId));
    }
    /** @internal */
    async _fetchItemCalculations(pItems, pInactiveItemMode = "Auto") {
        if (!this._isCaching())
            return (rejectError(ERR_INVALID_STATE, "caching not enabled"));
        const tProcedure = this._getPricingProcedure();
        if (tProcedure == null)
            return (rejectError(ERR_INVALID_STATE, "no active pricing procedure"));
        //special handling for merged views
        //we have to use two requests in this case, one for folders and one for other items
        //Note: SetArticles (which are also groups) are using a special calculation and thus do not need this handling
        const tViewConfig = this._getViewConfig();
        if (tViewConfig != null && tViewConfig.mergeMode !== "None" && pInactiveItemMode === "Auto") {
            const tItems = new Array();
            const tFolders = new Array();
            for (const tItem of pItems) {
                if (tItem.type === "Folder") {
                    tFolders.push(tItem);
                }
                else {
                    tItems.push(tItem);
                }
            }
            await Promise.all([
                this._fetchItemCalculations(tItems, "Include"),
                this._fetchItemCalculations(tFolders, "Exclude")
            ]);
            return;
        }
        //collect uncached items
        const tItemIds = this.getUncachedItemIds(pItems.map((item) => item.id), this.mCalculationCache);
        if (tItemIds.length <= 0)
            return (Promise.resolve());
        const tOptions = new basket.GetPriceCalculationSheetOptions();
        if (this.mViewId != null) {
            tOptions.viewId = this.mViewId;
        }
        //include price of alternative items
        if (pInactiveItemMode === "Include") {
            tOptions.excludeInactiveItems = false;
            tOptions.includeInactiveItems = true;
        }
        const tResult = await this.mSession.basket.getPriceCalculationSheets(tItemIds, tProcedure.name, tOptions);
        //fill cache
        for (const tItemSheet of tResult) {
            this.mCalculationCache.set(tItemSheet.id, new CalculationSheetInfo(tProcedure, tItemSheet.sheet));
        }
    }
    /** @internal */
    async _getCompositeCalculation(pMainArticle) {
        const tProcedure = this._getPricingProcedure();
        if (tProcedure == null)
            throw newError(ERR_INVALID_STATE, "no active pricing procedure");
        const tOptions = new basket.GetPriceCalculationSheetOptions();
        tOptions.wholeComposite = true;
        //ensure correct price for alternative articles
        tOptions.excludeInactiveItems = false;
        tOptions.includeInactiveItems = true;
        if (this.mViewId != null)
            tOptions.viewId = this.mViewId;
        const tSheet = await this.mSession.basket.getPriceCalculationSheet([pMainArticle.id], tProcedure.name, tOptions);
        /* The EAIWS does not return a quantity for group calculations.
            In this case we can fallback to the quantity of the main article.
            This is correct because the quantity of the sub articles is always equal to the main article.
        */
        if (tSheet.quantity == null) {
            const tFallbackCalc = await pMainArticle.getCalculation();
            tSheet.quantity = tFallbackCalc.sheet.quantity;
        }
        return (new CalculationSheetInfo(tProcedure, tSheet));
    }
    /** @internal */
    _getCachedItemCalculation(pItemId) {
        return (this.mCalculationCache.get(pItemId));
    }
    /** @internal */
    async _getItemFields(pItems, pColumnIds) {
        const tResultMap = new Map();
        if (pItems.length === 0 || pColumnIds.length === 0)
            return (tResultMap);
        const tOptions = new basket.GetItemFieldsOptions();
        tOptions.useDefault = false;
        tOptions.resolveURI = true;
        if (this.mViewId != null)
            tOptions.viewId = this.mViewId;
        const tFields = await this.mSession.basket.getItemFields(pItems.map((item) => item.id), pColumnIds, tOptions);
        for (const tField of tFields) {
            let tFieldMap = tResultMap.get(tField.itemId);
            if (tFieldMap == null) {
                tFieldMap = new Map();
                tResultMap.set(tField.itemId, tFieldMap);
            }
            tFieldMap.set(tField.columnId, tField.data);
        }
        return (tResultMap);
    }
    /** @internal */
    _beginCaching() {
        this.mCacheDepth++;
    }
    /** @internal */
    _endCaching() {
        this.mCacheDepth--;
        if (this.mCacheDepth <= 0) {
            this.mCacheDepth = 0;
            this.mPropsCache = new Map();
            this.mEaiwsItemCache = new Map();
            this.mCalculationCache = new Map();
            //dispose deleted items
            if (this._deletedItemsCache.size > 0) {
                this._deletedItemsCache.forEach((pItem) => pItem._dispose());
                this._deletedItemsCache = new Map();
            }
        }
    }
    /** @internal */
    _isCaching() {
        return (this.mCacheDepth > 0);
    }
    /** @internal */
    _addEaiwsItemToCache(pItem) {
        if (this._isCaching() && pItem != null)
            this.mEaiwsItemCache.set(pItem.itemId, pItem);
    }
    /** @internal */
    _addItemPropsToCache(pProps) {
        if (this._isCaching() && pProps != null && pProps.itemId != null)
            this.mPropsCache.set(pProps.itemId, pProps);
    }
    /** @internal */
    _addCalculationInfoToCache(pItemId, pInfo) {
        if (this._isCaching() && pInfo != null && pItemId != null)
            this.mCalculationCache.set(pItemId, pInfo);
    }
    /** @internal */
    _beginItemUpdate() {
        this.mItemUpdateDepth++;
        if (this.mItemUpdateDepth === 1) {
            this.mItemUpdatePromise = new Promise((pResolve) => {
                this.mItemUpdatePromiseResolve = pResolve;
            });
        }
    }
    /** @internal */
    _endItemUpdate() {
        this.mItemUpdateDepth--;
        if (this.mItemUpdateDepth <= 0) {
            this.mItemUpdateDepth = 0;
            this.mItemUpdatePromise = null;
            if (this.mItemUpdatePromiseResolve != null) {
                this.mItemUpdatePromiseResolve();
                this.mItemUpdatePromiseResolve = null;
            }
        }
    }
    /** @internal */
    _getHeaderCalcVersion() {
        return (this.mHeaderCalcVersion);
    }
    internalClear() {
        if (this.mRoot != null) {
            this.mItemMap = undefined; //speed up dispose
            this.mRoot._dispose();
            this.mRoot = undefined;
        }
        this.mHeaderCalculation = undefined;
        this.mHeaderCalcVersion = 0;
        this.mItemMap = new Map();
        this.mSelection = new Array();
    }
    async loadRootFolder() {
        try {
            this._beginCaching();
            const tOptions = new basket.GetAllItemsOptions();
            tOptions.setArticleIds = true;
            tOptions.geometryIds = true;
            if (this.mViewId != null)
                tOptions.viewId = this.mViewId;
            //get all items and fill cache
            const tItems = await this.mSession.basket.getAllItems(undefined, tOptions);
            for (const tItem of tItems)
                this._addEaiwsItemToCache(tItem);
            if (tItems.length === 0)
                throw new Error("Failed to create root.");
            //first item is always the root folder
            this.mRoot = await this._createItem(tItems[0].itemId, null, 0, false);
            this.eventItemCreated.trigger(this.mRoot);
        }
        finally {
            this._endCaching();
        }
    }
    selectionChanged() {
        this.mSelectionChanged = true;
        if (this.mSelectionChangeDepth === 0) {
            this.mSelectionChanged = false;
            this.eventSelectionChanged.trigger();
        }
    }
    getUncachedItemIds(pItemIds, pCache) {
        const tItemIds = new Array();
        for (const tId of pItemIds) {
            const tEntry = pCache.get(tId);
            if (tEntry === undefined)
                tItemIds.push(tId);
        }
        return (tItemIds);
    }
}
